/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid ui/header.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import { interpolate } from '../labels'
import type {
 DataGridColumn, DataGridLabels, DataGridSlot, DataGridTable, SortingState
} from '../types'
import { applyPinnedCell, type GridPinning } from './pinning'

const CLASS_NAME_HEADER = 'data-grid-header'
const CLASS_NAME_HEADER_CELL = 'data-grid-header-cell'
const CLASS_NAME_HEADER_LABEL = 'data-grid-header-label'
const CLASS_NAME_FILTER_ROW = 'data-grid-filter-row'
const CLASS_NAME_RESIZER = 'data-grid-resizer'
const CLASS_NAME_RESIZER_ACTIVE = 'data-grid-resizer-active'
const CLASS_NAME_HANDLE_CELL = 'data-grid-row-handle-cell'
const CLASS_NAME_SELECT_CELL = 'data-grid-select-cell'
const CLASS_NAME_SORTABLE = 'data-grid-sortable'
const CLASS_NAME_SORT_BUTTON = 'data-grid-sort-button'
const CLASS_NAME_SORT_ICON = 'data-grid-sort-icon'

export interface GridHeaderContext {
  // With cellNavigation on, the label row joins the roving-tabindex grid: its
  // cells carry grid roles and neither they nor their buttons are tabbable.
  // The filter row keeps native tab semantics (an input row, not grid cells).
  cellNavigation: boolean
  columns: DataGridColumn[]
  draggable: boolean
  filterRow: boolean
  hasMenu: (column: DataGridColumn) => boolean
  icons: {
    columnMenu: string
    filter: string
    sortAscending: string
    sortDescending: string
    sortNeutral: string
  }
  hasFilterMenu: (column: DataGridColumn) => boolean
  isFilterable: (column: DataGridColumn) => boolean
  isResizable: (column: DataGridColumn) => boolean
  isSortable: (column: DataGridColumn) => boolean
  labels: DataGridLabels
  resizable: boolean
  rowOrder: boolean
  selection: boolean
  selectAll: boolean
  table: DataGridTable
}

export interface GridHeaderState {
  // Columns with any active filter value - drives the filter button indicator.
  // Null when column filters are off.
  filteredColumns: string[] | null
  // Visible leaf column ids in table order (pinning + columnOrder + visibility).
  order: string[]
  pinning: GridPinning | null
  resizingColumn: string | false
  sizes: Record<string, number> | null
  sorting: SortingState
  selectAll: {
    checked: boolean
    indeterminate: boolean
  } | null
}

export interface GridHeader {
  element: HTMLTableSectionElement
  rowCount: number
  update: (state: GridHeaderState) => void
  dispose: () => void
}

type SortDirection = 'ascending' | 'descending' | 'neutral'

const sortDirection = (columnSort: SortingState[number] | undefined): SortDirection => {
  if (!columnSort) {
    return 'neutral'
  }

  return columnSort.desc ? 'descending' : 'ascending'
}

const iconForDirection = (direction: SortDirection, icons: GridHeaderContext['icons']): string => {
  if (direction === 'ascending') {
    return icons.sortAscending
  }

  if (direction === 'descending') {
    return icons.sortDescending
  }

  return icons.sortNeutral
}

const createSelectAllCell = (context: GridHeaderContext): { cell: HTMLTableCellElement; input: HTMLInputElement | null } => {
  const cell = document.createElement('th')
  cell.classList.add(CLASS_NAME_SELECT_CELL)
  cell.setAttribute('scope', 'col')

  if (!context.selectAll) {
    return { cell, input: null }
  }

  const input = document.createElement('input')
  input.type = 'checkbox'
  input.classList.add('form-check-input')
  input.setAttribute('aria-label', context.labels.selectAllRows)
  input.dataset.coreuiSelectAll = ''
  cell.append(input)

  return { cell, input }
}

// The handle column has no header of its own; the cell only reserves the
// width so the body's grips line up under it.
const createHandleHeaderCell = (): HTMLTableCellElement => {
  const cell = document.createElement('th')
  cell.classList.add(CLASS_NAME_HANDLE_CELL)
  cell.setAttribute('scope', 'col')
  return cell
}

const createResizer = (): HTMLElement => {
  const resizer = document.createElement('span')
  resizer.classList.add(CLASS_NAME_RESIZER)
  resizer.setAttribute('aria-hidden', 'true')
  resizer.dataset.coreuiResizer = ''
  return resizer
}

const createLabelRow = (
  context: GridHeaderContext,
  cells: Map<string, HTMLTableCellElement>,
  resizers: Map<string, HTMLElement>,
  sortIcons: Map<string, HTMLElement>
): {
  row: HTMLTableRowElement
  selectAllInput: HTMLInputElement | null
  leadingCells: HTMLTableCellElement[]
} => {
  const row = document.createElement('tr')
  let selectAllInput: HTMLInputElement | null = null
  const leadingCells: HTMLTableCellElement[] = []

  if (context.cellNavigation) {
    row.setAttribute('role', 'row')
  }

  if (context.rowOrder) {
    const cell = createHandleHeaderCell()
    if (context.cellNavigation) {
      cell.setAttribute('role', 'columnheader')
    }

    leadingCells.push(cell)
    row.append(cell)
  }

  if (context.selection) {
    const { cell, input } = createSelectAllCell(context)
    selectAllInput = input
    leadingCells.push(cell)
    if (context.cellNavigation) {
      cell.setAttribute('role', 'columnheader')
      if (input) {
        input.tabIndex = -1
      }
    }

    row.append(cell)
  }

  for (const column of context.columns) {
    const cell = document.createElement('th')
    cell.setAttribute('scope', 'col')
    cell.dataset.coreuiColumn = column.key
    if (context.cellNavigation) {
      cell.setAttribute('role', 'columnheader')
      cell.tabIndex = -1
    }

    if (column.style) {
      Object.assign(cell.style, column.style)
    }

    if (column.width !== undefined) {
      cell.style.width = `${column.width}px`
    }

    const content = document.createElement('div')
    content.classList.add(CLASS_NAME_HEADER_CELL)

    if (context.isSortable(column)) {
      cell.classList.add(CLASS_NAME_SORTABLE)
      const button = document.createElement('button')
      button.type = 'button'
      button.classList.add(CLASS_NAME_SORT_BUTTON)
      button.append(column.label ?? column.key)
      const icon = document.createElement('span')
      icon.classList.add(CLASS_NAME_SORT_ICON)
      icon.setAttribute('aria-hidden', 'true')
      icon.innerHTML = context.icons.sortNeutral
      icon.dataset.coreuiSort = 'neutral'
      button.append(icon)
      sortIcons.set(column.key, icon)
      content.append(button)
    } else {
      const label = document.createElement('span')
      label.classList.add(CLASS_NAME_HEADER_LABEL)
      label.textContent = column.label ?? column.key
      content.append(label)
    }

    if (context.hasFilterMenu(column)) {
      const button = document.createElement('button')
      button.type = 'button'
      button.classList.add('btn', 'btn-ghost', 'btn-sm', 'data-grid-filter-button')
      button.innerHTML = context.icons.filter
      button.dataset.coreuiFilter = column.key
      button.setAttribute('aria-haspopup', 'dialog')
      button.setAttribute('aria-expanded', 'false')
      const filterLabel = interpolate(context.labels.filterColumn, { column: column.label ?? column.key })
      button.setAttribute('aria-label', filterLabel)
      button.setAttribute('data-coreui-tooltip', filterLabel)
      content.append(button)
    }

    if (context.hasMenu(column)) {
      const button = document.createElement('button')
      button.type = 'button'
      button.classList.add('btn', 'btn-ghost', 'btn-sm', 'data-grid-menu-button')
      button.innerHTML = context.icons.columnMenu
      button.dataset.coreuiMenu = column.key
      button.setAttribute('aria-haspopup', 'menu')
      button.setAttribute('aria-expanded', 'false')
      const menuLabel = interpolate(context.labels.columnMenu, { column: column.label ?? column.key })
      button.setAttribute('aria-label', menuLabel)
      button.setAttribute('data-coreui-tooltip', menuLabel)
      content.append(button)
    }

    cell.append(content)

    if (context.cellNavigation) {
      for (const button of content.querySelectorAll('button')) {
        button.tabIndex = -1
      }
    }

    if (context.resizable && context.isResizable(column)) {
      const resizer = createResizer()
      resizers.set(column.key, resizer)
      cell.append(resizer)
    }

    cells.set(column.key, cell)
    row.append(cell)
  }

  return { row, selectAllInput, leadingCells }
}

const createFilterRow = (
  context: GridHeaderContext,
  filterCells: Map<string, HTMLTableCellElement>,
  filterSlots: DataGridSlot[]
): { row: HTMLTableRowElement; leadingCells: HTMLTableCellElement[] } => {
  const row = document.createElement('tr')
  row.classList.add(CLASS_NAME_FILTER_ROW)
  if (context.cellNavigation) {
    row.setAttribute('role', 'row')
  }

  const leadingCells: HTMLTableCellElement[] = []

  for (const leading of [context.rowOrder, context.selection]) {
    if (leading) {
      const cell = document.createElement('th')
      leadingCells.push(cell)
      row.append(cell)
    }
  }

  for (const column of context.columns) {
    const cell = document.createElement('th')
    const tableColumn = context.table.getColumn(column.key)
    if (context.isFilterable(column) && column.filter && tableColumn) {
      const slot = column.filter({ column: tableColumn, table: context.table, labels: context.labels })
      filterSlots.push(slot)
      cell.append(slot.element)
    }

    filterCells.set(column.key, cell)
    row.append(cell)
  }

  return { row, leadingCells }
}

export const createHeader = (context: GridHeaderContext): GridHeader => {
  const element = document.createElement('thead')
  element.classList.add(CLASS_NAME_HEADER)

  const cells = new Map<string, HTMLTableCellElement>()
  const resizers = new Map<string, HTMLElement>()
  const sortIcons = new Map<string, HTMLElement>()
  const filterCells = new Map<string, HTMLTableCellElement>()
  const filterSlots: DataGridSlot[] = []
  const movableColumns = new Map(context.columns.map(column => [column.key, column.movable !== false]))

  const { row, selectAllInput, leadingCells } = createLabelRow(context, cells, resizers, sortIcons)
  element.append(row)

  let filterRowElement: HTMLTableRowElement | null = null
  let filterLeadingCells: HTMLTableCellElement[] = []
  if (context.filterRow) {
    const filterRow = createFilterRow(context, filterCells, filterSlots)
    filterLeadingCells = filterRow.leadingCells
    filterRowElement = filterRow.row
    element.append(filterRow.row)
  }

  // Cells are created once per config column; ordering/visibility re-appends
  // the existing nodes (appending moves them), so sort buttons, resizers and
  // custom filter UIs keep their DOM state.
  let orderSignature: string | null = null
  const applyOrder = (order: string[]) => {
    const visible = new Set(order)
    const reorderRow = (
      rowElement: HTMLTableRowElement,
      cellMap: Map<string, HTMLTableCellElement>,
      leading: HTMLTableCellElement[]
    ) => {
      const ordered: HTMLTableCellElement[] = [...leading]
      for (const id of order) {
        const cell = cellMap.get(id)
        if (cell) {
          ordered.push(cell)
        }
      }

      rowElement.append(...ordered)
      if (context.cellNavigation) {
        for (const [index, cell] of ordered.entries()) {
          cell.setAttribute('aria-colindex', String(index + 1))
        }
      }

      for (const [key, cell] of cellMap) {
        if (!visible.has(key)) {
          cell.remove()
        }
      }
    }

    reorderRow(row, cells, leadingCells)
    if (filterRowElement) {
      reorderRow(filterRowElement, filterCells, filterLeadingCells)
    }
  }

  return {
    element,
    rowCount: context.filterRow ? 2 : 1,
    update(state) {
      const signature = state.order.join('|')
      if (signature !== orderSignature) {
        orderSignature = signature
        applyOrder(state.order)
      }

      for (const [key, cell] of cells) {
        const columnSort = state.sorting.find(sort => sort.id === key)
        const direction = sortDirection(columnSort)
        if (direction === 'neutral') {
          cell.removeAttribute('aria-sort')
        } else {
          cell.setAttribute('aria-sort', direction)
        }

        const sortIcon = sortIcons.get(key)
        if (sortIcon && sortIcon.dataset.coreuiSort !== direction) {
          sortIcon.dataset.coreuiSort = direction
          sortIcon.innerHTML = iconForDirection(direction, context.icons)
        }

        if (state.sizes && state.sizes[key] !== undefined) {
          cell.style.width = `${state.sizes[key]}px`
        }

        if (state.pinning) {
          applyPinnedCell(cell, state.pinning.columns[key] ?? null)
        }
      }

      // A column is draggable only when reordering is on and its pinned region
      // holds another movable column to swap with - reordering never crosses a
      // pinning boundary, so a column alone in its region has no valid target.
      if (context.draggable) {
        const regionOf = (key: string): string => state.pinning?.columns[key]?.side ?? 'center'
        const movablePerRegion = new Map<string, number>()
        for (const key of state.order) {
          if (movableColumns.get(key)) {
            const region = regionOf(key)
            movablePerRegion.set(region, (movablePerRegion.get(region) ?? 0) + 1)
          }
        }

        for (const [key, cell] of cells) {
          const draggable = Boolean(movableColumns.get(key)) && (movablePerRegion.get(regionOf(key)) ?? 0) > 1
          if (draggable) {
            cell.dataset.coreuiDraggable = ''
            // Without this the browser scrolls (or picks text) instead of
            // handing the pointer stream to the drag controller on touch.
            cell.style.touchAction = 'none'
          } else {
            delete cell.dataset.coreuiDraggable
            cell.style.touchAction = ''
          }
        }
      }

      for (const [key, resizer] of resizers) {
        resizer.classList.toggle(CLASS_NAME_RESIZER_ACTIVE, state.resizingColumn === key)
      }

      if (state.filteredColumns) {
        for (const [key, cell] of cells) {
          cell
            .querySelector('[data-coreui-filter]')
            ?.classList.toggle('data-grid-filter-button-filtered', state.filteredColumns.includes(key))
        }
      }

      if (state.pinning) {
        for (const [key, cell] of filterCells) {
          applyPinnedCell(cell, state.pinning.columns[key] ?? null)
        }

        // Every leading cell (the handle column, the select column) shares
        // the select column's pin — they travel together at the start edge.
        for (const cell of [...leadingCells, ...filterLeadingCells]) {
          applyPinnedCell(cell, state.pinning.select)
        }
      }

      if (selectAllInput && state.selectAll) {
        selectAllInput.checked = state.selectAll.checked
        selectAllInput.indeterminate = state.selectAll.indeterminate
      }

      for (const slot of filterSlots) {
        slot.update?.()
      }
    },
    dispose() {
      for (const slot of filterSlots) {
        slot.dispose?.()
      }
    }
  }
}
